import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Button,
  IconButton,
  Tabs,
  Tab,
  Box,
  Stack,
  CircularProgress,
  Typography,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  DialogContentText,
  Collapse,
  Chip,
  Card,
  CardHeader,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTheme } from '@mui/material/styles';
import {
  assignUsersToRole,
  assignMenusToRole,
  getRoleWithRelations,
} from '@/services/manageService/roleService';
import { queryMenuTree, type Menu } from '@/services/manageService/menuService';
import { queryUserPage } from '@/services/manageService/userService';
import { useButtonPerm } from '@/hooks/usePageCrud';
import userStore from '@/store/admin/userStore';
import { actionClassLabel, classifyMenuAction, type MenuActionClass } from '@/utils/menuActionClass';

interface RoleBindDrawerProps {
  open: boolean;
  roleId: number | undefined;
  /** Target role type; 0 = Super Admin */
  roleType?: number;
  roleName?: string;
  onClose: (payload?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => void;
}

type UserItem = { userId: number; userName: string; deptName?: string };

type TreeNode = Menu & { children?: TreeNode[] };

/** MUI Enhanced Transfer List helper: items in a but not in b. */
function not(a: readonly number[], b: readonly number[]): number[] {
  return a.filter((value) => !b.includes(value));
}

/** MUI Enhanced Transfer List helper: items present in both a and b. */
function intersection(a: readonly number[], b: readonly number[]): number[] {
  return a.filter((value) => b.includes(value));
}

/** MUI Enhanced Transfer List helper: unique union of a and b. */
function union(a: readonly number[], b: readonly number[]): number[] {
  return [...a, ...not(b, a)];
}

function collectDescendantIds(node: TreeNode): number[] {
  const ids: number[] = [];
  const walk = (n: TreeNode) => {
    (n.children || []).forEach((c) => {
      ids.push(Number(c.menuId));
      walk(c);
    });
  };
  walk(node);
  return ids;
}

function collectSelfAndDescendants(node: TreeNode): number[] {
  return [Number(node.menuId), ...collectDescendantIds(node)];
}

function collectAncestorIds(nodes: TreeNode[], targetId: number, path: number[] = []): number[] | null {
  for (const n of nodes) {
    const id = Number(n.menuId);
    const next = [...path, id];
    if (id === targetId) return path;
    const hit = collectAncestorIds(n.children || [], targetId, next);
    if (hit) return hit;
  }
  return null;
}

function flattenWithClass(nodes: TreeNode[]): Array<{ id: number; action: MenuActionClass; node: TreeNode }> {
  const out: Array<{ id: number; action: MenuActionClass; node: TreeNode }> = [];
  const walk = (list: TreeNode[]) => {
    list.forEach((n) => {
      out.push({
        id: Number(n.menuId),
        action: classifyMenuAction(n.realm, n.menuType),
        node: n,
      });
      if (n.children?.length) walk(n.children);
    });
  };
  walk(nodes);
  return out;
}

const RoleBindDrawer: React.FC<RoleBindDrawerProps> = ({ open, roleId, roleType, roleName, onClose }) => {
  const theme = useTheme();
  const canConfig = useButtonPerm('admin:relation:menu');
  const isCurrentSuperAdmin = userStore.user?.isAdmin === true;
  const isTargetSuperAdmin =
    roleType === 0 || String(roleName || '').toLowerCase() === 'superadmin';
  // Q4: only superadmin may configure superadmin role menus
  const canShowButtonsTab = canConfig && (!isTargetSuperAdmin || isCurrentSuperAdmin);

  const [tab, setTab] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [userIds, setUserIds] = React.useState<number[]>([]);
  const [menuIds, setMenuIds] = React.useState<number[]>([]);
  const [menuLoading, setMenuLoading] = React.useState(false);
  const [menuTree, setMenuTree] = React.useState<TreeNode[]>([]);
  const [expandedIds, setExpandedIds] = React.useState<Set<number>>(new Set());
  const [confirmOpen, setConfirmOpen] = React.useState<null | 'users' | 'menus'>(null);

  const [userPageNum, setUserPageNum] = React.useState(1);
  const [userPageSize] = React.useState(10);
  const [userTotal, setUserTotal] = React.useState(0);
  const [userLoading, setUserLoading] = React.useState(false);
  const [userList, setUserList] = React.useState<any[]>([]);
  /** Shared checked ids for MUI Enhanced Transfer List (both sides). */
  const [checkedUserIds, setCheckedUserIds] = React.useState<number[]>([]);

  // When Buttons tab is hidden, force Users tab
  const effectiveTab = canShowButtonsTab ? tab : 0;

  const userMap = React.useMemo(() => {
    const m = new Map<number, UserItem>();
    userList.forEach((u: any) => {
      const id = Number(u.userId ?? u.id);
      if (!Number.isNaN(id)) {
        m.set(id, {
          userId: id,
          userName: String(u.username ?? u.userName ?? u.firstName ?? id),
          deptName: u.deptName,
        });
      }
    });
    return m;
  }, [userList]);

  const rightUserItems: UserItem[] = React.useMemo(
    () => userIds.map((id) => userMap.get(id) || { userId: id, userName: String(id) }),
    [userIds, userMap],
  );
  const leftUserItems: UserItem[] = React.useMemo(
    () =>
      userList
        .map((u: any) => ({
          userId: Number(u.userId ?? u.id),
          userName: String(u.username ?? u.userName ?? u.firstName ?? u.id),
          deptName: u.deptName,
        }))
        .filter((u: UserItem) => !Number.isNaN(u.userId) && !userIds.includes(u.userId)),
    [userList, userIds],
  );

  const leftUserIds = React.useMemo(() => leftUserItems.map((u) => u.userId), [leftUserItems]);
  const rightSideIds = React.useMemo(() => rightUserItems.map((u) => u.userId), [rightUserItems]);
  const leftChecked = React.useMemo(
    () => intersection(checkedUserIds, leftUserIds),
    [checkedUserIds, leftUserIds],
  );
  const rightChecked = React.useMemo(
    () => intersection(checkedUserIds, rightSideIds),
    [checkedUserIds, rightSideIds],
  );

  const numberOfCheckedUsers = (ids: readonly number[]) => intersection(checkedUserIds, ids).length;

  const handleToggleUser = (id: number) => () => {
    setCheckedUserIds((prev) => {
      const currentIndex = prev.indexOf(id);
      if (currentIndex === -1) return [...prev, id];
      const next = [...prev];
      next.splice(currentIndex, 1);
      return next;
    });
  };

  const handleToggleAllUsers = (ids: readonly number[]) => () => {
    if (numberOfCheckedUsers(ids) === ids.length) {
      setCheckedUserIds((prev) => not(prev, ids));
    } else {
      setCheckedUserIds((prev) => union(prev, ids));
    }
  };

  const moveUsersRight = () => {
    if (!leftChecked.length) return;
    setUserIds((prev) => Array.from(new Set([...prev, ...leftChecked])));
    setCheckedUserIds((prev) => not(prev, leftChecked));
  };

  const moveUsersLeft = () => {
    if (!rightChecked.length) return;
    setUserIds((prev) => prev.filter((id) => !rightChecked.includes(id)));
    setCheckedUserIds((prev) => not(prev, rightChecked));
  };

  const renderUserTransferList = (
    title: React.ReactNode,
    items: UserItem[],
    footer?: React.ReactNode,
  ) => {
    const ids = items.map((u) => u.userId);
    return (
      <Card
        variant="outlined"
        sx={{
          flex: '1 1 0',
          minWidth: 0,
          maxWidth: 360,
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <CardHeader
          sx={{ px: 2, py: 1, flexShrink: 0 }}
          avatar={
            <Checkbox
              onClick={handleToggleAllUsers(ids)}
              checked={numberOfCheckedUsers(ids) === ids.length && ids.length !== 0}
              indeterminate={numberOfCheckedUsers(ids) !== ids.length && numberOfCheckedUsers(ids) !== 0}
              disabled={ids.length === 0}
              inputProps={{ 'aria-label': 'all items selected' }}
            />
          }
          title={title}
          subheader={`${numberOfCheckedUsers(ids)}/${ids.length} selected`}
        />
        <Divider />
        <List
          dense
          component="div"
          role="list"
          sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'background.paper' }}
        >
          {items.map((u) => {
            const labelId = `role-user-transfer-item-${u.userId}-label`;
            return (
              <ListItemButton
                key={u.userId}
                role="listitem"
                onClick={handleToggleUser(u.userId)}
              >
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <Checkbox
                    size="small"
                    checked={checkedUserIds.includes(u.userId)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                </ListItemIcon>
                <ListItemText
                  id={labelId}
                  primary={u.userName || `User ${u.userId}`}
                  secondary={`ID: ${u.userId}`}
                />
              </ListItemButton>
            );
          })}
        </List>
        {footer}
      </Card>
    );
  };

  const idsToText = (ids: number[]) => ids.join(',');
  const checkedSet = React.useMemo(() => new Set(menuIds), [menuIds]);

  const loadData = async () => {
    if (!roleId) return;
    setLoading(true);
    try {
      const roleWithRels: any = await getRoleWithRelations(roleId);
      const toNum = (arr: any[]) =>
        Array.from(new Set((arr || []).map((x: any) => Number(x)).filter((n) => !Number.isNaN(n))));
      const userCandidates: any[] = [
        roleWithRels?.userIds,
        roleWithRels?.users?.map((u: any) => u?.userId ?? u?.id),
        roleWithRels?.roleUsers?.map((u: any) => u?.userId ?? u?.id),
      ].filter(Boolean);
      const usersRaw = (userCandidates.find((arr: any) => Array.isArray(arr) && arr.length > 0) ??
        userCandidates.find(Array.isArray) ??
        []) as any[];
      const menuCandidates: any[] = [
        roleWithRels?.menuIds,
        roleWithRels?.menus?.map((m: any) => m?.menuId ?? m?.id),
        roleWithRels?.roleMenus?.map((m: any) => m?.menuId ?? m?.id),
        roleWithRels?.menuIdList,
        roleWithRels?.roleMenuIds,
        roleWithRels?.menuList?.map((m: any) => m?.menuId ?? m?.id),
      ].filter(Boolean);
      const menusRaw = (menuCandidates.find((arr: any) => Array.isArray(arr) && arr.length > 0) ??
        menuCandidates.find(Array.isArray) ??
        []) as any[];
      setUserIds(toNum(Array.isArray(usersRaw) ? usersRaw : []));
      setMenuIds(toNum(Array.isArray(menusRaw) ? menusRaw : []));
    } catch {
      // keep empty defaults
    } finally {
      setLoading(false);
    }
  };

  const loadUserPage = async (page: number) => {
    setUserLoading(true);
    try {
      const payload = { pageNumber: page, pageSize: userPageSize, pageType: 1 };
      const resp: any = await queryUserPage(payload);
      const data = resp?.data ?? resp;
      const records = data?.list || data?.records || resp?.records || [];
      const total = data?.totalRow ?? data?.total ?? resp?.total ?? 0;
      const pageNum = data?.pageNumber ?? data?.pageNum ?? resp?.pageNum ?? page;
      setUserList(Array.isArray(records) ? records : []);
      setUserTotal(Number(total) || 0);
      setUserPageNum(Number(pageNum) || page);
    } catch {
      setUserList([]);
      setUserTotal(0);
    } finally {
      setUserLoading(false);
    }
  };

  const handleLoadMenus = async () => {
    setMenuLoading(true);
    try {
      // Full catalog tree including menu_type=3
      const resp: any = await queryMenuTree({ pid: 0 });
      const data = resp?.data !== undefined ? resp.data : resp;
      const roots: TreeNode[] = Array.isArray(data) ? data : [];
      setMenuTree(roots);
      // Expand first level by default
      setExpandedIds(new Set(roots.map((r) => Number(r.menuId))));
    } catch {
      setMenuTree([]);
    } finally {
      setMenuLoading(false);
    }
  };

  React.useEffect(() => {
    if (open) {
      setTab(0);
      setMenuTree([]);
      setCheckedUserIds([]);
      loadData();
      loadUserPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roleId]);

  React.useEffect(() => {
    if (open && canShowButtonsTab && effectiveTab === 1 && menuTree.length === 0 && !menuLoading) {
      handleLoadMenus();
    }
  }, [open, canShowButtonsTab, effectiveTab, menuTree.length, menuLoading]);

  const handleSaveUsers = async () => {
    if (!roleId) return;
    setSaving(true);
    try {
      await assignUsersToRole(roleId, userIds);
      onClose({ refreshed: true, message: 'Bind users successful', severity: 'success' });
    } catch (e: any) {
      onClose({ refreshed: false, message: e?.message || 'Bind users failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMenus = async () => {
    if (!roleId || !canShowButtonsTab) return;
    setSaving(true);
    try {
      await assignMenusToRole(roleId, menuIds);
      onClose({ refreshed: true, message: 'Bind menus successful', severity: 'success' });
    } catch (e: any) {
      onClose({ refreshed: false, message: e?.message || 'Bind menus failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const setChecked = (ids: number[], checked: boolean) => {
    setMenuIds((prev) => {
      const s = new Set(prev);
      ids.forEach((id) => {
        if (checked) s.add(id);
        else s.delete(id);
      });
      return Array.from(s);
    });
  };

  const toggleNode = (node: TreeNode, nextChecked: boolean) => {
    const id = Number(node.menuId);
    if (nextChecked) {
      const ancestors = collectAncestorIds(menuTree, id) || [];
      const selfAndKids =
        node.menuType === 1 || node.menuType === 2
          ? collectSelfAndDescendants(node)
          : [id];
      setChecked([...ancestors, ...selfAndKids], true);
    } else if (node.menuType === 1 || node.menuType === 2) {
      setChecked(collectSelfAndDescendants(node), false);
    } else {
      setChecked([id], false);
    }
  };

  const applyShortcut = (pageNode: TreeNode, mode: 'view' | 'viewExport' | 'rwe' | 'clear') => {
    const flat = flattenWithClass([pageNode]);
    const pageId = Number(pageNode.menuId);
    if (mode === 'clear') {
      setChecked(collectSelfAndDescendants(pageNode), false);
      return;
    }
    const keep = new Set<number>([pageId]);
    flat.forEach(({ id, action }) => {
      if (id === pageId) return;
      if (mode === 'view' && action === 'read') keep.add(id);
      if (mode === 'viewExport' && (action === 'read' || action === 'export')) keep.add(id);
      if (mode === 'rwe' && (action === 'read' || action === 'create' || action === 'update' || action === 'remove' || action === 'export')) {
        keep.add(id);
      }
    });
    const allUnder = collectSelfAndDescendants(pageNode);
    setMenuIds((prev) => {
      const s = new Set(prev);
      allUnder.forEach((id) => s.delete(id));
      keep.forEach((id) => s.add(id));
      const ancestors = collectAncestorIds(menuTree, pageId) || [];
      ancestors.forEach((id) => s.add(id));
      return Array.from(s);
    });
  };

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderTree = (nodes: TreeNode[], depth: number): React.ReactNode => {
    return nodes.map((node) => {
        const id = Number(node.menuId);
        const kids = node.children || [];
        const hasKids = kids.length > 0;
        const expanded = expandedIds.has(id);
        const checked = checkedSet.has(id);
        const desc = collectDescendantIds(node);
        const checkedKids = desc.filter((d) => checkedSet.has(d)).length;
        const indeterminate = !checked && checkedKids > 0;
        const action = classifyMenuAction(node.realm, node.menuType);
        const isPage = node.menuType === 1 || node.menuType === 2;
        return (
          <Box key={id} sx={{ pl: depth * 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minHeight: 32 }}>
              {hasKids ? (
                <IconButton size="small" onClick={() => toggleExpand(id)}>
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              ) : (
                <Box sx={{ width: 28 }} />
              )}
              <Checkbox
                size="small"
                checked={checked}
                indeterminate={indeterminate}
                onChange={(_, v) => toggleNode(node, v)}
              />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {node.menuName || node.realm || id}
              </Typography>
              <Chip size="small" label={actionClassLabel(action)} sx={{ height: 20, fontSize: 11 }} />
              {isPage && (
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }} onClick={() => applyShortcut(node, 'view')}>
                    View
                  </Button>
                  <Button size="small" sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }} onClick={() => applyShortcut(node, 'viewExport')}>
                    View+E
                  </Button>
                  <Button size="small" sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }} onClick={() => applyShortcut(node, 'rwe')}>
                    RW+E
                  </Button>
                  <Button size="small" sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }} onClick={() => applyShortcut(node, 'clear')}>
                    Clear
                  </Button>
                </Stack>
              )}
            </Stack>
            {hasKids && (
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                {renderTree(kids, depth + 1)}
              </Collapse>
            )}
          </Box>
        );
      });
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => onClose()}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 720, md: 800 },
            maxWidth: '100vw',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ m: 0, p: 2, flexShrink: 0 }}
        >
          <Typography variant="h6" fontWeight={600}>
            Bind Relations{roleName ? ` — ${roleName}` : ''}
          </Typography>
          <IconButton aria-label="close" onClick={() => onClose()} sx={{ color: theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ flexShrink: 0 }} />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" flex={1} minHeight={0}>
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Tabs
                value={effectiveTab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 2, flexShrink: 0 }}
              >
                <Tab label="Role-Users" />
                {canShowButtonsTab && <Tab label="Role-Buttons" />}
              </Tabs>

              {effectiveTab === 0 && (
                <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      gap: 2,
                      alignItems: 'stretch',
                    }}
                  >
                    {userLoading ? (
                      <Card
                        variant="outlined"
                        sx={{
                          flex: '1 1 0',
                          minWidth: 0,
                          maxWidth: 360,
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CircularProgress size={20} />
                      </Card>
                    ) : (
                      renderUserTransferList(
                        'Unauthorized',
                        leftUserItems,
                        userTotal > userPageSize ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="flex-end"
                            sx={{ flexShrink: 0, px: 1, py: 1, borderTop: 1, borderColor: 'divider' }}
                          >
                            <Button size="small" disabled={userLoading || userPageNum <= 1} onClick={() => loadUserPage(userPageNum - 1)}>
                              Prev
                            </Button>
                            <Typography variant="caption">
                              Page {userPageNum} / {Math.max(1, Math.ceil(userTotal / userPageSize))}
                            </Typography>
                            <Button
                              size="small"
                              disabled={userLoading || userPageNum * userPageSize >= userTotal}
                              onClick={() => loadUserPage(userPageNum + 1)}
                            >
                              Next
                            </Button>
                          </Stack>
                        ) : undefined,
                      )
                    )}
                    <Stack spacing={1} sx={{ flexShrink: 0, justify: 'flex', justifyContent: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={moveUsersRight}
                        disabled={leftChecked.length === 0}
                        aria-label="move selected to authorized"
                      >
                        &gt;
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={moveUsersLeft}
                        disabled={rightChecked.length === 0}
                        aria-label="move selected to unauthorized"
                      >
                        &lt;
                      </Button>
                    </Stack>
                    {renderUserTransferList('Authorized', rightUserItems)}
                  </Box>
                  <Divider sx={{ flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    Selected userIds: {idsToText(userIds)}
                  </Typography>
                </Stack>
              )}

              {canShowButtonsTab && effectiveTab === 1 && (
                <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    P=page R=read C=create U=update D=delete E=export S=special. Shortcuts on page rows only.
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                      overflow: 'auto',
                    }}
                  >
                    {menuLoading ? (
                      <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
                    ) : menuTree.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No menus</Typography>
                    ) : (
                      renderTree(menuTree, 0)
                    )}
                  </Box>
                  <Divider sx={{ flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    Selected menuIds: {menuIds.length}
                  </Typography>
                </Stack>
              )}
            </Box>
          )}
        </Box>
        <Divider sx={{ flexShrink: 0 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
          {effectiveTab === 0 && (
            <Button
              variant="contained"
              color="primary"
              disabled={saving}
              onClick={() => setConfirmOpen('users')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius }}
            >
              Save Users
            </Button>
          )}
          {canShowButtonsTab && effectiveTab === 1 && (
            <Button
              variant="contained"
              color="primary"
              disabled={saving}
              onClick={() => setConfirmOpen('menus')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius }}
            >
              Save Menus
            </Button>
          )}
        </Stack>
      </Drawer>

      <Dialog open={!!confirmOpen} onClose={() => setConfirmOpen(null)}>
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmOpen === 'users' && `Bind ${userIds.length} user(s) to this role?`}
            {confirmOpen === 'menus' && `Overwrite ${menuIds.length} menu/button grant(s) for this role?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(null)}>Cancel</Button>
          {confirmOpen === 'users' && (
            <Button onClick={() => { setConfirmOpen(null); handleSaveUsers(); }} variant="contained">Confirm</Button>
          )}
          {confirmOpen === 'menus' && (
            <Button onClick={() => { setConfirmOpen(null); handleSaveMenus(); }} variant="contained">Confirm</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RoleBindDrawer;
